import { Player } from '../domain/entities';

// Simple rules-based system for offline role generation.
const getAttributeScore = (player: Player, attributes: string[]) => {
    if (attributes.length === 0) return 0;
    return attributes.reduce((sum, attr) => sum + (player.attributes[attr] || 0), 0) / attributes.length;
};

const ROLES = {
    GK: [
        { name: 'Goalkeeper', description: 'A standard goalkeeper focused on the basics of shot-stopping and handling.', required: [] },
        { name: 'Sweeper Keeper', description: 'An aggressive goalkeeper who comes off their line to intercept through balls and start attacks.', required: ['pace', 'passing'] },
    ],
    DEF: [
        { name: 'Central Defender', description: 'A solid defender focused on tackling, marking, and winning headers.', required: ['tackling', 'marking', 'heading'] },
        { name: 'Ball-Playing Defender', description: 'A defender who is comfortable on the ball, capable of starting attacks from the back.', required: ['passing', 'creativity'] },
        { name: 'Full-Back', description: 'A wide defender who balances defensive duties with supporting attacks on the flank.', required: ['pace', 'tackling'] },
        { name: 'Wing-Back', description: 'A more attacking version of a full-back, providing width high up the pitch.', required: ['pace', 'dribbling'] },
    ],
    MID: [
        { name: 'Defensive Midfielder', description: 'A midfielder who shields the defence, focusing on tackling and breaking up opposition play.', required: ['tackling', 'positioning'] },
        { name: 'Deep-Lying Playmaker', description: 'A midfielder who controls the game from a deep position with their exceptional passing range.', required: ['passing', 'creativity'] },
        { name: 'Box-to-Box Midfielder', description: 'An all-action midfielder who contributes in both defence and attack.', required: ['tackling', 'passing', 'finishing'] },
        { name: 'Attacking Midfielder', description: 'A creative force in the final third, focused on creating and scoring goals.', required: ['creativity', 'finishing', 'dribbling'] },
        { name: 'Winger', description: 'A wide midfielder who uses pace and dribbling to beat defenders and deliver crosses.', required: ['pace', 'dribbling'] },
    ],
    FWD: [
        { name: 'Poacher', description: 'A forward who comes alive in the penalty box, clinical with their finishing.', required: ['finishing', 'positioning'] },
        { name: 'Target Man', description: 'A strong forward who excels in the air, holding up the ball and bringing teammates into play.', required: ['heading', 'dribbling'] }, // Dribbling is a proxy for strength/hold-up play in our attribute system.
        { name: 'Deep-Lying Forward', description: 'A forward who drops deep to link play between the midfield and attack.', required: ['passing', 'creativity'] },
        { name: 'Advanced Forward', description: 'A pacy forward who leads the line, runs in behind, and stretches the opposition defence.', required: ['pace', 'finishing'] },
    ]
};

export const RoleService = {
    getOfflinePlayerRole: (player: Player, tacticalPosition: string): { roleName: string, roleDescription: string } => {
        const rolesForPosition = ROLES[player.position];
        if (!rolesForPosition) {
            return { roleName: 'Player', roleDescription: 'A player in the squad.' };
        }

        let bestRole = rolesForPosition[0];
        let maxScore = 0;

        rolesForPosition.forEach(role => {
            if (role.required.length === 0) return;
            const score = getAttributeScore(player, role.required);
            if (score > maxScore) {
                maxScore = score;
                bestRole = role;
            }
        });

        // Add tactical position context
        if (player.position === 'DEF' && (tacticalPosition.includes('L') || tacticalPosition.includes('R'))) {
             if (tacticalPosition.includes('WB')) {
                 bestRole = rolesForPosition.find(r => r.name === 'Wing-Back') || bestRole;
             } else {
                 bestRole = rolesForPosition.find(r => r.name === 'Full-Back') || bestRole;
             }
        }
        if (player.position === 'MID' && (tacticalPosition.includes('L') || tacticalPosition.includes('R'))) {
            bestRole = rolesForPosition.find(r => r.name === 'Winger') || bestRole;
        }

        return { roleName: bestRole.name, roleDescription: bestRole.description };
    }
};
